import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import VistaprintAuth from '@vp/auth';
import type { Locale } from '../i18n/config';

export interface AuthContextProps {
    auth: typeof VistaprintAuth.WebAuth | undefined;
    canonicalId: string | undefined;
    anonymousId: string | undefined;
}

const AuthContext = createContext<AuthContextProps>({} as AuthContextProps);

interface AuthContextProviderProps {
    locale: Locale;
    children: ReactNode;
}

export type AuthType = typeof VistaprintAuth.WebAuth | undefined;

const AuthContextProvider = ({ locale, children }: AuthContextProviderProps) => {
    const [auth, setAuthState] = useState<AuthType>(undefined);
    const [canonicalId, setCanonicalId] = useState<string | undefined>(undefined);
    const [anonymousId, setAnonymousId] = useState<string | undefined>(undefined);

    useEffect(() => {
        VistaprintAuth.init(getAuthConfig(locale), () => {
            const webAuth = new VistaprintAuth.WebAuth();
            setAuthState(webAuth);
        });
    }, [locale]); // Reinitialize auth when locale changes

    useEffect(() => {
        if (auth) {
            setCanonicalId(auth.getCanonicalId());

            if (!auth.isSignedIn()) {
                setAnonymousId(auth.getCanonicalId());
            } else {
                const recentAnonymousIds = auth.getRecentAnonymousIds();
                setAnonymousId(recentAnonymousIds?.length ? recentAnonymousIds[0] : undefined);
            }
        }
    }, [auth]);

    return (
        <AuthContext.Provider
            value={{
                auth,
                canonicalId,
                anonymousId,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

const useAuthContext = (): AuthContextProps => {
    const authContext = useContext(AuthContext);
    if (!authContext) {
        throw new Error('useAuthContext must be used within an AuthContextProvider');
    }
    return authContext;
};

function getAuthConfig(culture: Locale) {
    if (!window.location.origin.includes('vistaprint')) {
        return {
            developmentMode: true,
            culture,
        };
    }

    return {
        culture,
        options: {
            allowGuestUser: false,
            redirectAfterSignOutCallback: () => window.location.href,
        },
    };
}

export { useAuthContext, AuthContextProvider };
